/**
 * MapActionOverlay renders a transparent, absolutely-positioned row of icon
 * buttons pinned above a point on the map, and dismisses itself when the
 * pointer leaves.
 *
 * It exists because two things now need exactly this: a selected marker
 * (edit / draw line / delete) and a selected tactical line (edit / delete).
 * The fiddly part is the pinning — container coordinates recomputed on
 * `move` / `zoom` / `moveend` / `zoomend` so the row tracks the map through
 * pans, pinches and inertia — and that is worth having in one place rather
 * than copied per entity type.
 *
 * Must be mounted inside a `MapContainer` so `useMap()` returns the live map.
 */

import React, { useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

/** Size of each action button (px). */
export const BUTTON_SIZE = 32;
/** Horizontal gap between buttons (px). */
export const BUTTON_GAP = 8;
/** Vertical offset above the anchor point (px), so the row clears the symbol. */
export const VERTICAL_OFFSET = 24;

export interface MapAction {
    /** Icon shown in the button. */
    icon: IconDefinition;
    /** Accessible name, e.g. "Delete marker". */
    label: string;
    /** `data-testid` for the button. */
    testId: string;
    /** Destructive actions get the error-red treatment. */
    danger?: boolean;
    onClick: () => void;
}

export interface MapActionOverlayProps {
    /** Map position the row is pinned above, or null to render nothing. */
    anchor: { lat: number; lng: number } | null;
    actions: MapAction[];
    onDismiss: () => void;
    /** `data-testid` for the row itself. */
    testId: string;
}

export default function MapActionOverlay({
    anchor,
    actions,
    onDismiss,
    testId,
}: MapActionOverlayProps) {
    const map = useMap();
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const lat = anchor?.lat;
    const lng = anchor?.lng;

    useEffect(() => {
        if (lat == null || lng == null) {
            setPos(null);
            return;
        }

        const updatePosition = () => {
            const point = map.latLngToContainerPoint([lat, lng]);
            setPos({ x: point.x, y: point.y });
        };

        // Initial placement
        updatePosition();

        // Keep the overlay pinned while the map moves. `move` fires continuously
        // during pan, `zoom` during pinch/wheel; the *end variants finalize
        // after inertia settles.
        map.on("move", updatePosition);
        map.on("zoom", updatePosition);
        map.on("moveend", updatePosition);
        map.on("zoomend", updatePosition);
        return () => {
            map.off("move", updatePosition);
            map.off("zoom", updatePosition);
            map.off("moveend", updatePosition);
            map.off("zoomend", updatePosition);
        };
    }, [map, lat, lng]);

    if (lat == null || lng == null || !pos || actions.length === 0) {
        return null;
    }

    const overlayWidth =
        BUTTON_SIZE * actions.length + BUTTON_GAP * (actions.length - 1);
    const overlayHeight = BUTTON_SIZE;

    // Centre horizontally on the anchor; lift above it so the row does not
    // obscure what was selected.
    const left = pos.x - overlayWidth / 2;
    const top = pos.y - overlayHeight - VERTICAL_OFFSET;

    // Stop clicks reaching the MapContainer, which would otherwise open the
    // add-marker modal in EDIT mode or drop a vertex while drawing.
    const swallow = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <div
            data-testid={testId}
            onMouseLeave={onDismiss}
            onClick={swallow}
            onMouseDown={swallow}
            onDoubleClick={swallow}
            style={{
                position: "absolute",
                left: `${left}px`,
                top: `${top}px`,
                width: `${overlayWidth}px`,
                height: `${overlayHeight}px`,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: `${BUTTON_GAP}px`,
                background: "transparent",
                pointerEvents: "auto",
                zIndex: 1000,
            }}
        >
            {actions.map((action) => (
                <button
                    key={action.testId}
                    type="button"
                    aria-label={action.label}
                    data-testid={action.testId}
                    onClick={(e) => {
                        e.stopPropagation();
                        action.onClick();
                    }}
                    style={{
                        width: `${BUTTON_SIZE}px`,
                        height: `${BUTTON_SIZE}px`,
                        border: action.danger
                            ? "1px solid rgba(220, 53, 69, 0.35)"
                            : "1px solid rgba(0, 0, 0, 0.25)",
                        borderRadius: "4px",
                        color: action.danger ? "#dc3545" : undefined,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                    }}
                >
                    <FontAwesomeIcon icon={action.icon} />
                </button>
            ))}
        </div>
    );
}
